import { UnstyledButton, alpha } from '@mantine/core';
import { FC, MouseEvent, RefObject, useMemo, useRef } from 'react';

import Paragraph from '@/components/text/Paragraph.tsx';

import useConfig from '@/hooks/useConfig.ts';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { Appointment, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { CalendarInfo, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DCCalendar, Timings } from '@/models/doc-cirrus/DCCalendar.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import {
    CALENDAR_COLUMNS,
    CalendarColumnType,
    getCalendarForAppointment,
    getClickAreas,
    getIntervalConfig,
    getTimeSlotInfo,
    isFullDayAppointment,
    placeSeriesAppointment,
} from '@/utils/calendarUtils.ts';
import { getInstanceConfig } from '@/utils/configUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openWalkInModal } from '@/utils/modalUtils.tsx';
import { cn } from '@/utils/styleUtils.ts';

import AllDayAppointment from './AllDayAppointment.tsx';
import GroupedAppointmentsList from './GroupedAppointmentsList.tsx';
import SkeletonAppointmentCard from './SkeletonAppointmentCard.tsx';
import TimeNowIndicator from './TimeNowIndicator.tsx';

interface Props {
    columnTitle?: string;
    appointments: DailyAppointment[];
    date?: Date;
    timeslotHeight: number;
    containerHeight: number;
    timings?: Timings;
    timeSlots: { time: string }[];
    calendar?: Partial<DCCalendar>;
    columnType: CalendarColumnType;
    allCalendars?: CalendarInfo[];
    groupedAppointments: Record<string, DailyAppointment[]>;
    scheduleTypeColorMap: Record<string, string>;
    mutateAppointments: () => void;
    practitioner?: Practitioner; // Added for OOO checking
}

const CalendarColumn: FC<Props> = ({
    columnTitle,
    appointments,
    timeslotHeight,
    containerHeight,
    timings,
    date,
    timeSlots,
    calendar,
    columnType,
    allCalendars,
    groupedAppointments,
    scheduleTypeColorMap,
    mutateAppointments,
    practitioner,
}) => {
    const selectedDate = useFilterStore((state) => state.date);
    const columnRef = useRef<HTMLInputElement | undefined>(undefined);
    const { location_key } = useConfig();
    const selectedCalendars = useFilterStore((state) => state.selected_calendars);
    const currentCalendarSelection = selectedCalendars[location_key] || [];
    const timelineInterval = useCalendarStore((state) => state.timeline_interval);
    const isSeriesPlacementMode = useBookingStore((state) => state.isSeriesPlacementMode);
    const seriesPlacedAppointments = useBookingStore((state) => state.seriesPlacedAppointments);

    const columnDate = date || selectedDate;
    const columnCalendarId = calendar?.id;
    const seriesPlacementsForColumn = useMemo(() => {
        if (seriesPlacedAppointments.length === 0) return [];
        return seriesPlacedAppointments.filter((a) => {
            const dateMatch = dayjs(a.startDatetime).format('YYYY-MM-DD') === dayjs(columnDate).format('YYYY-MM-DD');
            if (a.calendarId) return dateMatch && a.calendarId === columnCalendarId;
            return dateMatch;
        });
    }, [seriesPlacedAppointments, columnDate, columnCalendarId]);

    const { click_segments } = getIntervalConfig(timelineInterval);

    const availableSlots = useMemo(() => {
        return timeSlots.map((slot) => {
            const slotInfo = getTimeSlotInfo(
                slot.time,
                timings,
                date ?? selectedDate ?? undefined,
                practitioner,
                timelineInterval
            );
            return {
                ...slot,
                is_available: slotInfo.is_available,
                segments: slotInfo.segments,
            };
        });
    }, [timeSlots, timings, date, selectedDate, practitioner, timelineInterval]);

    // Generate click areas based on segments count
    const clickAreas = getClickAreas(click_segments, timelineInterval);

    // Handle click on a specific time slot
    const handleSlotClick = (
        _event: MouseEvent<HTMLButtonElement>,
        selectedSlotTime: string,
        minuteOffset: number
    ): void => {
        const slotTime = dayjs(selectedSlotTime, 'HH:mm').add(minuteOffset, DayjsUnit.MINUTE);

        const today = dayjs().startOf(DayjsUnit.DAY);
        const selectedDay = dayjs(columnDate).startOf(DayjsUnit.DAY);

        // If the selected date is in the past, use today's date; otherwise use the selected date
        const appointmentBaseDate = selectedDay.isBefore(today) ? today : selectedDay;

        // Create appointment date with the slot time
        const appointmentDate = appointmentBaseDate
            .hour(slotTime.hour())
            .minute(slotTime.minute())
            .startOf(DayjsUnit.MINUTE);

        // Series placement mode — place appointment instead of opening modals
        if (isSeriesPlacementMode) {
            placeSeriesAppointment({ columnDate: columnDate, slotTime: slotTime, calendarId: calendar?.id });
            return;
        }

        const instanceConfig = getInstanceConfig(calendar?.instance_id ?? '');

        const [calendarId, practiceDocCirrusId] = getCalendarForAppointment(
            columnType,
            currentCalendarSelection,
            calendar,
            allCalendars
        );

        openWalkInModal(() => {
            openAppointmentScheduler({
                mode: 'book',
                prefilled_values: {
                    pvs_provider: instanceConfig?.pvs_provider ?? PVSProvider.DOC_CIRRUS,
                    date_time: appointmentDate.toISOString(),
                    calendar_pvs_id: calendarId,
                    practice_doc_cirrus_id: practiceDocCirrusId,
                },
            });
        }, mutateAppointments);
    };

    const appointmentColors = useMemo(() => {
        if (!scheduleTypeColorMap) return {};

        const colors: Record<Appointment['id'], string> = {};

        for (const appointment of appointments) {
            if (!colors[appointment.id]) {
                if (appointment.color) {
                    colors[appointment.id] = appointment.color;
                    continue;
                }

                const scheduleTypeId = appointment.links?.schedule_type_id || appointment.data_cur_links?.event_type_id;

                if (scheduleTypeId && scheduleTypeColorMap[scheduleTypeId]) {
                    colors[appointment.id] = scheduleTypeColorMap[scheduleTypeId];
                }
            }
        }

        return colors;
    }, [appointments, scheduleTypeColorMap]);

    const isToday = useMemo(() => {
        return dayjs(date).isSame(dayjs(), DayjsUnit.DAY);
    }, [date]);

    const isPastColumnInPlacement = isSeriesPlacementMode && !!date && dayjs(date).isBefore(dayjs(), DayjsUnit.DAY);

    // Separate normal and all day appointments
    const allDayAppointments = useMemo(() => {
        const allDay = appointments.filter(isFullDayAppointment);

        return allDay;
    }, [appointments]);

    // Use shared header height directly from the store
    const headerHeight = useCalendarStore((state) => state.header_height);

    return (
        <div
            ref={columnRef as RefObject<HTMLDivElement>}
            className="relative h-full w-full border-r border-slate-200"
            style={{ minWidth: `calc(100cqw / ${CALENDAR_COLUMNS} - 13px)` }}
        >
            <TimeNowIndicator timeslotHeight={timeslotHeight} containerHeight={containerHeight} />
            <div
                className="sticky top-0 z-10 flex flex-col bg-white shadow-sm"
                style={{ height: headerHeight ? `${headerHeight}px` : 'auto' }}
            >
                <div className="flex items-center justify-center py-1">
                    <Paragraph className="z-21 max-w-[270px] overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold text-slate-500">
                        {columnType === CalendarColumnType.WEEK ? (
                            <>
                                {`${dayjs(date).format('dddd')} `}
                                <span
                                    className={cn(
                                        isToday &&
                                            'inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary'
                                    )}
                                >
                                    {dayjs(date).format('D')}
                                </span>
                            </>
                        ) : (
                            columnTitle
                        )}
                    </Paragraph>
                </div>

                {/* Display all-day appointments in the header */}
                {allDayAppointments.length > 0 && (
                    <div className="flex flex-col gap-1 overflow-y-auto px-1 pt-1">
                        {allDayAppointments.map((appointment) => (
                            <AllDayAppointment
                                key={appointment.id}
                                appointment={appointment}
                                color={appointmentColors[appointment.id]}
                                allCalendars={allCalendars}
                            />
                        ))}
                    </div>
                )}
            </div>
            {availableSlots.map((slot) => {
                return (
                    <div
                        key={slot.time}
                        className="relative flex flex-col border-t border-slate-200 bg-slate-100 last:border-b last:border-slate-200"
                        style={{ height: `${timeslotHeight}px` }}
                    >
                        {/* Background segments with colors */}
                        {slot.segments.map((segment) => (
                            <div
                                key={`${slot.time}-${segment.timing_id}-${segment.start_minute}-${segment.end_minute}`}
                                className="absolute"
                                style={{
                                    backgroundColor: alpha(
                                        segment.color,
                                        practitioner?.pvs_provider === PVSProvider.DATA_CUR ? 1 : 0.3
                                    ),
                                    top: `${(segment.start_minute / timelineInterval) * 100}%`,
                                    height: `${((segment.end_minute - segment.start_minute) / timelineInterval) * 100}%`,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        ))}

                        {/* Dynamic clickable overlay buttons */}
                        {clickAreas.map((area) => (
                            <UnstyledButton
                                key={`${slot.time}-${area.offset}`}
                                onClick={(e) => handleSlotClick(e, slot.time, area.offset)}
                                className="relative w-full cursor-pointer rounded hover:bg-blue-100 hover:bg-opacity-50 hover:shadow-md"
                                style={{ height: area.height }}
                            />
                        ))}
                    </div>
                );
            })}
            {Object.keys(groupedAppointments).length > 0 && (
                <GroupedAppointmentsList
                    key={date?.toString()}
                    groupedAppointments={groupedAppointments}
                    timeslotHeight={timeslotHeight}
                    containerHeight={containerHeight}
                    appointmentColors={appointmentColors}
                    date={date}
                    mutateAppointments={mutateAppointments}
                />
            )}
            {seriesPlacementsForColumn.map((placement) => (
                <SkeletonAppointmentCard
                    key={placement.id}
                    placedAppointment={placement}
                    containerHeight={containerHeight}
                    timeslotHeight={timeslotHeight}
                />
            ))}
            {isPastColumnInPlacement && (
                <div
                    className="pointer-events-none absolute inset-0 z-[2] bg-slate-200/50"
                    style={{ top: headerHeight ?? 0 }}
                />
            )}
        </div>
    );
};

export default CalendarColumn;
